import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import _JSXStyle from "styled-jsx/style";
import { useContext, useState } from "react";
import classnames from "classnames";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { TIERS } from "constants/pricing";
import { CREDITS_PER_TIER, getCreditsPerSeatAmount, getSharedCreditsPoolAmount, shouldShowSharedCreditsPoolRow } from "constants/pricing/credits-config";
import PricingInfoIcon from "/static/svg/pricing-info-icon.svg";
import TooltipComponent from "components/enhancements/tooltip/tooltip-component";
import { DARK_THEME } from "components/enhancements/tooltip/tooltip-constants";
import { trackEvent } from "services/bigbrain-service";
import { PRICING_PAGE_AI_PLAN_SUMMARY_TOGGLED, PRICING_PAGE_AI_PLAN_SUMMARY_FEATURE_HOVERED } from "constants/bigbrain-event-types";
import { tierAiBlock } from "./tier-ai-block-component.scss";
import { AI_COLUMNS_ICON_URL, AI_FEATURES_CONFIG, CRM_AI_FEATURES, CRM_AI_FEATURES_TIER_EXCLUSIONS, CRM_INFO_TOOLTIP_KEY, CREDITS_PER_SEAT_AI_FEATURES, CREDITS_PER_SEAT_ENTERPRISE_CUSTOM_KEY, CREDITS_PER_SEAT_ENTERPRISE_TITLE_KEY, CREDITS_PER_SEAT_GOVERNANCE_PERMISSIONS_KEY, CREDITS_PER_SEAT_GOVERNANCE_PERMISSIONS_TOOLTIP_KEY, CREDITS_PER_SEAT_GOVERNANCE_SPEND_LIMITS_KEY, CREDITS_PER_SEAT_GOVERNANCE_SPEND_LIMITS_TOOLTIP_KEY, CREDITS_PER_SEAT_GOVERNANCE_TITLE_KEY, CREDITS_PER_SEAT_LABEL_KEY, CREDITS_PER_SEAT_LABEL_TOOLTIP_KEY, CREDITS_PER_SEAT_AMOUNT_SUFFIX_KEY, CREDITS_PER_SEAT_SHARED_POOL_AMOUNT_SUFFIX_KEY, CREDITS_PER_SEAT_SHARED_POOL_LABEL_KEY, CREDITS_PER_SEAT_SHARED_POOL_UNAVAILABLE_KEY, CREDITS_PER_SEAT_TOOLS_COUNT_KEY, getCreditsPerSeatFeatureLabelKey, INFO_TOOLTIP_KEY, LIMITED_CHIP_KEY, SIDEKICK_ICON_URL, SUBTITLE_TEMPLATE_KEY, TIER_AI_FEATURES, TIER_BLOCK_CONFIG } from "./tier-ai-block-constants";
import LockedSvg from "./svgs/locked-svg";
import BasicAiSvg from "./svgs/basic-ai-svg";
import OpenSvg from "./svgs/open-svg";
import MinimizeSvg from "./svgs/minimize-svg";
import ChevronSvg from "./svgs/chevron-svg";
import EnterpriseAiSvg from "./svgs/enterprise-ai-svg";
import SecuritySvg from "./svgs/security-svg";
import { Accounts, Person } from "@mondaydotcomorg/icons";
const renderTierIcon = (variant)=>{
    switch(variant){
        case "lock":
            return /*#__PURE__*/ _jsx(LockedSvg, {});
        case "basic":
            return /*#__PURE__*/ _jsx(BasicAiSvg, {});
        case "sidekick":
            return /*#__PURE__*/ _jsx("img", {
                src: SIDEKICK_ICON_URL,
                alt: "",
                width: "20",
                height: "20"
            });
        case "ai-columns":
            return /*#__PURE__*/ _jsx("img", {
                src: AI_COLUMNS_ICON_URL,
                alt: "",
                width: "20",
                height: "20"
            });
        case "enterprise-ai":
            return /*#__PURE__*/ _jsx(EnterpriseAiSvg, {});
        default:
            return null;
    }
};
const TierAiBlock = ({ tierName, simplified = false, titleOverride, defaultExpanded = false, creditsPerSeat = false, seats })=>{
    const { translate, localeId } = useContext(GenericConfigContext);
    const config = TIER_BLOCK_CONFIG[tierName];
    // CRM classic (simplified) mode opens the box only via `defaultExpanded` (the
    // pro-expanded AB arm) — `config.defaultOpen` drives the work-management
    // credits layout and must not force CRM boxes open. WM keeps its per-tier
    // defaultOpen behavior.
    const [isExpanded, setIsExpanded] = useState(simplified ? defaultExpanded : (config === null || config === void 0 ? void 0 : config.defaultOpen) || false);
    if (!config) return null;
    const isEnterprise = tierName === TIERS.ENTERPRISE;
    const blockTitle = creditsPerSeat && isEnterprise ? translate(CREDITS_PER_SEAT_ENTERPRISE_TITLE_KEY) : titleOverride || translate(config.titleKey);
    if (config.variant === "disabled") {
        return /*#__PURE__*/ _jsxs("div", {
            className: `jsx-${tierAiBlock.__hash}` + " " + "tier-ai-block is-disabled",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: `jsx-${tierAiBlock.__hash}` + " " + "block-header static",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-header-left",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "tier-icon",
                                children: renderTierIcon(config.icon)
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "block-text",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-title",
                                        children: blockTitle
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-subtitle",
                                        children: translate(config.subtitleKey)
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(_JSXStyle, {
                    id: tierAiBlock.__hash,
                    children: tierAiBlock
                })
            ]
        });
    }
    if (creditsPerSeat && !simplified) {
        return /*#__PURE__*/ _jsx(CreditsPerSeatAiBlock, {
            tierName: tierName,
            isEnterprise: isEnterprise,
            blockTitle: blockTitle,
            isExpanded: isExpanded,
            setIsExpanded: setIsExpanded,
            translate: translate,
            localeId: localeId,
            seats: seats
        });
    }
    const excludedKeys = CRM_AI_FEATURES_TIER_EXCLUSIONS[tierName] || [];
    const features = simplified ? CRM_AI_FEATURES.filter(({ key })=>!excludedKeys.includes(key)) : TIER_AI_FEATURES[tierName] || [];
    const toolsCount = features.length;
    const credits = CREDITS_PER_TIER[tierName] || 0;
    const subtitleTemplateKey = config.subtitleTemplateKey || SUBTITLE_TEMPLATE_KEY;
    const subtitleText = (translate(subtitleTemplateKey) || "{toolsCount} tools · {credits} AI credits / month").replace("{toolsCount}", toolsCount).replace("{credits}", credits.toLocaleString(localeId || undefined));
    const getFeatureLabel = (featureKey)=>translate(AI_FEATURES_CONFIG[featureKey].labelKey);
    // In simplified (CRM classic) mode only Pro carries the gradient border; every
    // other tier renders with the plain gray border. WM keeps its own highlight map.
    const isHighlighted = simplified ? tierName === TIERS.PRO : config.highlighted;
    // Mirrors the pricing-hub `mf-the-pricing-page` AI card events so both surfaces
    // report the same shape: info1 = tier, info2 = expanded/collapsed state or the
    // stable feature key (locale-independent, so tracking survives copy changes).
    const handleToggle = ()=>{
        const next = !isExpanded;
        trackEvent(PRICING_PAGE_AI_PLAN_SUMMARY_TOGGLED, {
            info1: tierName,
            info2: next ? "expanded" : "collapsed"
        });
        setIsExpanded(next);
    };
    const handleFeatureHover = (featureKey)=>{
        trackEvent(PRICING_PAGE_AI_PLAN_SUMMARY_FEATURE_HOVERED, {
            info1: tierName,
            info2: featureKey
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: `jsx-${tierAiBlock.__hash}` + " " + (classnames("tier-ai-block", "is-collapsible", {
            "is-highlighted": isHighlighted,
            "is-simplified": simplified,
            "is-expanded": isExpanded
        }) || ""),
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                "aria-expanded": isExpanded,
                onClick: handleToggle,
                className: `jsx-${tierAiBlock.__hash}` + " " + "block-header",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-header-left",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "tier-icon",
                                children: renderTierIcon(config.icon)
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "block-text",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-title",
                                        children: [
                                            blockTitle,
                                            /*#__PURE__*/ _jsxs("div", {
                                                onClick: (e)=>e.stopPropagation(),
                                                className: `jsx-${tierAiBlock.__hash}` + " " + "info-icon-wrapper",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: `jsx-${tierAiBlock.__hash}` + " " + "tooltip",
                                                        children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                                            theme: DARK_THEME,
                                                            className: "floating-tooltip",
                                                            children: translate(simplified ? CRM_INFO_TOOLTIP_KEY : INFO_TOOLTIP_KEY)
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(PricingInfoIcon, {
                                                        className: "info-icon"
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    !simplified && /*#__PURE__*/ _jsx("div", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-subtitle",
                                        children: subtitleText
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "expand-toggle",
                        children: simplified ? /*#__PURE__*/ _jsx(ChevronSvg, {}) : isExpanded ? /*#__PURE__*/ _jsx(MinimizeSvg, {}) : /*#__PURE__*/ _jsx(OpenSvg, {})
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `jsx-${tierAiBlock.__hash}` + " " + (classnames("block-content-wrapper", {
                    "is-open": isExpanded
                }) || ""),
                children: /*#__PURE__*/ _jsx("div", {
                    className: `jsx-${tierAiBlock.__hash}` + " " + "block-content-overflow",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-content",
                        children: [
                            !simplified && config.headingKey && /*#__PURE__*/ _jsx("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "block-heading",
                                children: translate(config.headingKey)
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "features-list",
                                children: features.map(({ key, labelKey, isLimited })=>{
                                    const simplifiedDescKey = `pricingPage.crmAiBlock.usage.${key}.description`;
                                    const hasSimplifiedDesc = translate(simplifiedDescKey) !== simplifiedDescKey;
                                    return simplified ? /*#__PURE__*/ _jsx("li", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "feature-row",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: `jsx-${tierAiBlock.__hash}` + " " + "feature-row-left",
                                            children: /*#__PURE__*/ _jsxs("div", {
                                                onMouseEnter: ()=>handleFeatureHover(key),
                                                className: `jsx-${tierAiBlock.__hash}` + " " + "feature-label-wrapper",
                                                children: [
                                                    hasSimplifiedDesc && /*#__PURE__*/ _jsx("div", {
                                                        className: `jsx-${tierAiBlock.__hash}` + " " + "tooltip",
                                                        children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                                            theme: DARK_THEME,
                                                            className: "floating-tooltip",
                                                            children: translate(simplifiedDescKey)
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: `jsx-${tierAiBlock.__hash}` + " " + "feature-label",
                                                        children: translate(labelKey)
                                                    })
                                                ]
                                            })
                                        })
                                    }, key) : /*#__PURE__*/ _jsx("li", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "feature-row",
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: `jsx-${tierAiBlock.__hash}` + " " + "feature-row-left",
                                            children: [
                                                /*#__PURE__*/ _jsx("img", {
                                                    src: AI_FEATURES_CONFIG[key].icon,
                                                    alt: "",
                                                    className: `jsx-${tierAiBlock.__hash}` + " " + "feature-icon"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    onMouseEnter: ()=>handleFeatureHover(key),
                                                    className: `jsx-${tierAiBlock.__hash}` + " " + "feature-label-wrapper",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: `jsx-${tierAiBlock.__hash}` + " " + "tooltip",
                                                            children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                                                theme: DARK_THEME,
                                                                className: "floating-tooltip",
                                                                children: translate(`pricingPage.credits.usage.${key}.description`)
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: `jsx-${tierAiBlock.__hash}` + " " + "feature-label",
                                                            children: getFeatureLabel(key)
                                                        })
                                                    ]
                                                }),
                                                isLimited && /*#__PURE__*/ _jsx("span", {
                                                    className: `jsx-${tierAiBlock.__hash}` + " " + "feature-chip",
                                                    children: translate(LIMITED_CHIP_KEY)
                                                })
                                            ]
                                        })
                                    }, key);
                                })
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(_JSXStyle, {
                id: tierAiBlock.__hash,
                children: tierAiBlock
            })
        ]
    });
};
const CreditsPerSeatAiBlock = ({ tierName, isEnterprise, blockTitle, isExpanded, setIsExpanded, translate, localeId, seats })=>{
    const features = CREDITS_PER_SEAT_AI_FEATURES[tierName] || [];
    const toolsCount = features.length;
    const creditsAmount = getCreditsPerSeatAmount(tierName);
    const showSharedPool = shouldShowSharedCreditsPoolRow(tierName);
    const sharedPoolAmount = showSharedPool ? getSharedCreditsPoolAmount(tierName, seats) : undefined;
    const isSharedPoolUnavailable = showSharedPool && sharedPoolAmount == null;
    const toolsLabel = (translate(CREDITS_PER_SEAT_TOOLS_COUNT_KEY) || "{toolsCount} tools").replace("{toolsCount}", String(toolsCount));
    const handleToggle = ()=>{
        const next = !isExpanded;
        trackEvent(PRICING_PAGE_AI_PLAN_SUMMARY_TOGGLED, {
            info1: tierName,
            info2: next ? "expanded" : "collapsed"
        });
        setIsExpanded(next);
    };
    const handleFeatureHover = (featureKey)=>{
        trackEvent(PRICING_PAGE_AI_PLAN_SUMMARY_FEATURE_HOVERED, {
            info1: tierName,
            info2: featureKey
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: `jsx-${tierAiBlock.__hash}` + " " + (classnames("tier-ai-block", "is-collapsible", "is-credits-per-seat", "is-highlighted", {
            "is-expanded": isExpanded
        }) || ""),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: `jsx-${tierAiBlock.__hash}` + " " + "block-header static credits-per-seat-header",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: `jsx-${tierAiBlock.__hash}` + " " + "block-title",
                    children: [
                        blockTitle,
                        /*#__PURE__*/ _jsxs("div", {
                            className: `jsx-${tierAiBlock.__hash}` + " " + "info-icon-wrapper",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: `jsx-${tierAiBlock.__hash}` + " " + "tooltip",
                                    children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                        theme: DARK_THEME,
                                        className: "floating-tooltip",
                                        children: translate(CREDITS_PER_SEAT_LABEL_TOOLTIP_KEY)
                                    })
                                }),
                                /*#__PURE__*/ _jsx(PricingInfoIcon, {
                                    className: "info-icon"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: `jsx-${tierAiBlock.__hash}` + " " + "credits-per-seat-metrics",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-head",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-icon",
                                        children: /*#__PURE__*/ _jsx(Person, {
                                            size: 14,
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-label",
                                        children: isEnterprise ? translate(CREDITS_PER_SEAT_ENTERPRISE_CUSTOM_KEY) : translate(CREDITS_PER_SEAT_LABEL_KEY)
                                    })
                                ]
                            }),
                            !isEnterprise && creditsAmount != null && /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-value",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-amount",
                                        children: creditsAmount.toLocaleString(localeId || undefined)
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-suffix",
                                        children: translate(CREDITS_PER_SEAT_AMOUNT_SUFFIX_KEY)
                                    })
                                ]
                            })
                        ]
                    }),
                    showSharedPool && /*#__PURE__*/ _jsx("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-per-seat-divider"
                    }),
                    showSharedPool && /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + (classnames("credits-metric", {
                            "is-unavailable": isSharedPoolUnavailable
                        }) || ""),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-head",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-icon",
                                        children: /*#__PURE__*/ _jsx(Accounts, {
                                            size: 14,
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-label",
                                        children: translate(CREDITS_PER_SEAT_SHARED_POOL_LABEL_KEY)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-value",
                                children: isSharedPoolUnavailable ? /*#__PURE__*/ _jsx("span", {
                                    className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-unavailable",
                                    children: translate(CREDITS_PER_SEAT_SHARED_POOL_UNAVAILABLE_KEY) || "--"
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-amount",
                                            children: sharedPoolAmount.toLocaleString(localeId || undefined)
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: `jsx-${tierAiBlock.__hash}` + " " + "credits-metric-suffix",
                                            children: translate(CREDITS_PER_SEAT_SHARED_POOL_AMOUNT_SUFFIX_KEY)
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                ]
            }),
            isEnterprise && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-per-seat-divider"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "governance-section",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "governance-title-row",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-per-seat-icon",
                                        children: /*#__PURE__*/ _jsx(SecuritySvg, {})
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "governance-title",
                                        children: translate(CREDITS_PER_SEAT_GOVERNANCE_TITLE_KEY)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "governance-detail credits-per-seat-hover-target",
                                children: [
                                    translate(CREDITS_PER_SEAT_GOVERNANCE_PERMISSIONS_KEY),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-per-seat-side-tooltip",
                                        children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                            theme: DARK_THEME,
                                            className: "floating-tooltip",
                                            children: translate(CREDITS_PER_SEAT_GOVERNANCE_PERMISSIONS_TOOLTIP_KEY)
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: `jsx-${tierAiBlock.__hash}` + " " + "governance-detail credits-per-seat-hover-target",
                                children: [
                                    translate(CREDITS_PER_SEAT_GOVERNANCE_SPEND_LIMITS_KEY),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "credits-per-seat-side-tooltip",
                                        children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                            theme: DARK_THEME,
                                            className: "floating-tooltip",
                                            children: translate(CREDITS_PER_SEAT_GOVERNANCE_SPEND_LIMITS_TOOLTIP_KEY)
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `jsx-${tierAiBlock.__hash}` + " " + "credits-per-seat-divider"
            }),
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                "aria-expanded": isExpanded,
                onClick: handleToggle,
                className: `jsx-${tierAiBlock.__hash}` + " " + "tools-toggle",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "tools-toggle-label",
                        children: toolsLabel
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + (classnames("expand-toggle", {
                            "is-open": isExpanded
                        }) || ""),
                        children: /*#__PURE__*/ _jsx(ChevronSvg, {})
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `jsx-${tierAiBlock.__hash}` + " " + (classnames("block-content-wrapper", {
                    "is-open": isExpanded
                }) || ""),
                children: /*#__PURE__*/ _jsx("div", {
                    className: `jsx-${tierAiBlock.__hash}` + " " + "block-content-overflow",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: `jsx-${tierAiBlock.__hash}` + " " + "block-content",
                        children: /*#__PURE__*/ _jsx("ul", {
                            className: `jsx-${tierAiBlock.__hash}` + " " + "features-list",
                            children: features.map(({ key })=>/*#__PURE__*/ _jsx("li", {
                                    className: `jsx-${tierAiBlock.__hash}` + " " + "feature-row",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: `jsx-${tierAiBlock.__hash}` + " " + "feature-row-left",
                                        children: [
                                            /*#__PURE__*/ _jsx("img", {
                                                src: AI_FEATURES_CONFIG[key].icon,
                                                alt: "",
                                                className: `jsx-${tierAiBlock.__hash}` + " " + "feature-icon"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                onMouseEnter: ()=>handleFeatureHover(key),
                                                className: `jsx-${tierAiBlock.__hash}` + " " + "feature-label-wrapper",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: `jsx-${tierAiBlock.__hash}` + " " + "tooltip",
                                                        children: /*#__PURE__*/ _jsx(TooltipComponent, {
                                                            theme: DARK_THEME,
                                                            className: "floating-tooltip",
                                                            children: translate(`pricingPage.credits.usage.${key}.description`)
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: `jsx-${tierAiBlock.__hash}` + " " + "feature-label",
                                                        children: translate(getCreditsPerSeatFeatureLabelKey(tierName, key))
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                }, key))
                        })
                    })
                })
            }),
            /*#__PURE__*/ _jsx(_JSXStyle, {
                id: tierAiBlock.__hash,
                children: tierAiBlock
            })
        ]
    });
};
export default TierAiBlock;
